<template>
  <el-dialog
    v-if="dialogVisible"
    class="address-dialog"
    :visible.sync="dialogVisible"
    append-to-body
    destroy-on-close
    :show-close="false"
  >
    <address-list
      v-if="!isEditAddress"
      v-model="selected"
      :data="data"
      :subscription-id="subscriptionId"
      :cancel="onCancel"
      is-dialog
      :submit="onSubmit"
      :corporate-flag="corporateFlag"
      :address-list-checkboxes="addressListCheckboxes"
      :selected-changed="selectedChanged"
      :is-ec-line027-flag="false"
      :has-added-address-flag="hasAddedAddressFlag"
      @add-address="openShippingAddressDialog"
      @edit-address="editAddress"
    />
    <template v-else>
      <the-heading :is-modal="true">{{
        addressEditId ? $t('ec-line011.updateHeading') : $t('ec-line011.addHeading')
      }}</the-heading>
      <shipping-form
        :id="addressEditId"
        @submit="onSubmitEdit"
        @cancel="onCancelEdit"
        @delete="onDeleteAddress"
        @update-inquiry-status="updateInquiryStatus"
      />
    </template>
  </el-dialog>
</template>

<script>
import AddressList from '@/components/AddressList.vue'
import ShippingForm from '@/components/ec-line011/ShippingForm.vue'
import { AddressService, AuthService, InquiryService } from '@/services'
import { PROVIDER, INQUIRY_STATUS_ID, INQUIRY_TYPE_ID, DEFAULT_COUNTRY_ID } from '@/constants'
import { trimCommaForm } from '@/utils'
import TheHeading from '@/components/my-page-partials/TheHeading.vue'

const target_type_ids = [INQUIRY_TYPE_ID.EMAIL, INQUIRY_TYPE_ID.LINE]

const target_status_ids = [INQUIRY_STATUS_ID.DONE_CORRESPOND]

export default {
  components: { AddressList, ShippingForm, TheHeading },
  props: {
    visible: Boolean,
    data: {
      type: Array,
      required: false,
      default: () => [],
    },
    value: {
      type: [String, Number],
      required: false,
      default: () => '',
    },
    subscriptionId: {
      type: [String, Number],
      default: () => '',
    },
    requestUpdateCustomerAddress: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      selected: this.value,
      isEditAddress: false,
      addressEditId: null,
      addressListCheckboxes: {
        is_update_customer: false,
        is_default: false,
      },
      hasAddedAddressFlag: false,
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(value) {
        this.$emit('update:visible', value)
      },
    },
    corporateFlag() {
      return this.$store.getters['auth/shop']?.corporate_flag
    },
    selectedChanged() {
      return this.selected !== this.value
    },
  },
  watch: {
    value(val) {
      this.selected = val
    },
    isEditAddress(val) {
      if (!val) {
        this.addressEditId = null
      }
    },
    dialogVisible: {
      handler(newVal) {
        if (newVal) {
          this.addressListCheckboxes = { is_update_customer: false, is_default: false }
          document.body.classList.add('modal-open')
        } else {
          document.body.classList.remove('modal-open')
        }
      },
      immediate: true,
    },
  },
  methods: {
    onCancel() {
      this.selected = this.value

      this.dialogVisible = false
    },
    onSubmit(selected) {
      if (this.addressListCheckboxes.is_update_customer && !this.corporateFlag) {
        this.onAcceptUpdateCustomerInfo()
      } else if (this.addressListCheckboxes.is_default) {
        this.loading = true
        this.setDefaultAddress(selected)
        this.onChooseAddress()
      } else {
        this.onChooseAddress()
      }

      this.selected = selected
    },
    onAcceptUpdateCustomerInfo() {
      const loading = this.$loading({ lock: true })

      const cusInfo = this.data.find((addr) => addr.id === this.selected)

      if (cusInfo) {
        const form = {
          name01: cusInfo.name01,
          name02: cusInfo.name02,
          kana01: cusInfo.kana01,
          kana02: cusInfo.kana02,
          postal_code: cusInfo.postal_code,
          addr01: cusInfo.addr01,
          addr02: cusInfo.addr02,
          addr03: cusInfo.addr03,
          phone_number: cusInfo.phone_number,
          pref_id: cusInfo.pref.id,
          customer_status: this.user.customer_status_id,
        }

        AuthService.updateInfo(this.user.id, form)
          .then(() => this.$store.dispatch('auth/getAuth', { provider: PROVIDER.CUSTOMER }))
          .then(() => this.$emit('update-customer'))
          .then(() => {
            if (this.addressListCheckboxes.is_default) {
              return AddressService.setDefaultAddress(this.user.id, this.selected, { is_default: true })
            }
          })
          .then(() => {
            this.onChooseAddress()
          })
          .then(() => {
            this.$alert({
              message: this.$t('ec-line011.successUpdateMessage'),
              type: 'success',
            })
          })
          .finally(() => loading.close())
      } else {
        loading.close()
        this.onChooseAddress()
      }
    },
    onChooseAddress() {
      this.$emit('input', this.selected)
      this.dialogVisible = false
    },

    openShippingAddressDialog() {
      this.hasAddedAddressFlag = false
      this.isEditAddress = true
    },

    editAddress(id) {
      this.addressEditId = id

      this.isEditAddress = true
    },

    async onSubmitEdit(data) {
      const loading = this.$loading({ lock: true })
      try {
        if (!this.corporateFlag) {
          data.company_name = null
          data.company_name_kana = null
        }
        const body = trimCommaForm({
          ...data,
          country_id: DEFAULT_COUNTRY_ID,
          customer_id: this.user.id,
          address_id: this.addressEditId,
          store_name: this.user.store_name,
          update_date: null,
          prefix_history: this.user.id,
          business_account_flag: this.user.business_account_flag,
          ...(this.addressEditId && { prefix_history_type: this.subscriptionId }),
        })

        let newAddressId
        if (this.addressEditId) {
          await AddressService.updateAddress(this.user.id, this.addressEditId, body)
        } else {
          const response = await AddressService.newAddress(body)
          newAddressId = response.data.id
          this.hasAddedAddressFlag = true
        }

        if (data.is_update_customer) {
          await this.$store.dispatch('auth/getAuth', { provider: PROVIDER.CUSTOMER })
          this.$emit('update-customer')
        }

        this.addressEditId ? (this.selected = this.addressEditId) : (this.selected = newAddressId)

        this.addressEditId
          ? this.$alert({ message: this.$t('ec-line011.successUpdateMessage'), type: 'success' })
          : this.$alert({ message: this.$t('ec-line011.successAddMessage'), type: 'success' })

        this.isEditAddress = false
        this.$emit('updated-address', this.addressEditId)
      } catch (error) {
        this.handleError({ message: error.response?.data?.meta?.message })
      } finally {
        loading.close()
      }
    },

    onCancelEdit() {
      this.isEditAddress = false
    },
    onDeleteAddress() {
      const loading = this.$loading({ lock: true })
      AddressService.deleleAddress(this.addressEditId)
        .then(() => {
          this.$alert({ message: this.$t('ec-line011.deleteShippingSucces'), type: 'success' })
          this.onCancelEdit()
          this.$emit('updated-address')
        })
        .catch((error) => this.handleError({ message: error.response?.data?.meta?.message }))
        .finally(() => loading.close())
    },
    async updateInquiryStatus() {
      const body = {
        status_id: this.Constants.INQUIRY_STATUS_ID.DURING_CORRESPOND,
        target_status_ids: target_status_ids,
        target_type_ids: target_type_ids,
        customer_id: this.user.id,
        customer_flag: true,
      }

      InquiryService.updateCustomerInquiryStatus(body)
    },
    setDefaultAddress(selected) {
      AddressService.setDefaultAddress(this.user.id, selected, { is_default: true })
        .then(() => (this.successVisible = true))
        .catch((error) => this.handleError(error))
        .finally(() => (this.loading = false))
    },
  },
}
</script>

<style lang="scss">
@import '@/assets/css/components/address-dialog.scss';
</style>
